import React, { useEffect, useState } from 'react'
import { Alert, Col, Row, Spinner } from 'react-bootstrap'
import moment from 'moment'
import objectPath from 'object-path'

import httpClient, { getBaseURL } from 'utility/httpClient.util'

const getData = (response) => response?.data?.data || response?.data || {}
const getFirst = (value) => Array.isArray(value) ? value[0] || {} : value || {}
const getText = (...values) => values.find((value) =>
  (typeof value === 'string' && value.trim()) || typeof value === 'number')
const display = (value) => value === undefined || value === null || value === '' ? '-' : value

const getAge = (person, row) => {
  const age = person?.age || row?.age
  if (typeof age === 'string' || typeof age === 'number') return age
  if (age?.year !== undefined || age?.years !== undefined) {
    return [age.year ?? age.years, 'ปี', age.month ?? age.months, 'เดือน'].filter((part) => part !== undefined && part !== null).join(' ')
  }
  const birthDate = getText(person?.birthDate, person?.dateOfBirth, person?.dob)
  if (!birthDate || !moment(birthDate).isValid()) return '-'
  const visitDate = moment(row?.visitDate).isValid() ? moment(row.visitDate) : moment()
  const years = visitDate.diff(birthDate, 'years')
  const months = visitDate.clone().subtract(years, 'years').diff(birthDate, 'months')
  return `${years} ปี ${months} เดือน`
}

const SCREENING_PREFIXES = [
  '', 'history.', 'history.body.tabPanelRlynM.', 'history.body.tabbF7om.tabPanelRlynM.',
  'body.tabPanelRlynM.', 'body.tabbF7om.tabPanelRlynM.', 'tabbF7om.tabPanelRlynM.',
  'body.tabTreatment.', 'body.tabbF7om.tabTreatment.',
]

const getScreeningValue = (screening, fields) => {
  const source = screening?.value && typeof screening.value === 'object'
    ? { ...screening, ...screening.value } : screening
  for (const field of fields) {
    for (const prefix of SCREENING_PREFIXES) {
      const value = objectPath.get(source, `${prefix}${field}`)
      if (value !== undefined && value !== null && value !== '') return value
    }
  }
  return undefined
}

const getAddress = (address, person) => {
  const item = getFirst(address)
  const parts = [
    getText(item.addressNo, item.houseNo, item.address, person?.address),
    item.moo ? `หมู่ ${item.moo}` : '', item.soi ? `ซอย ${item.soi}` : '',
    item.road ? `ถนน ${item.road}` : '',
    getText(item.addressSubdistrict?.name, item.subdistrict?.name, item.subdistrictName, person?.subDistrictName),
    getText(item.addressDistrict?.name, item.district?.name, item.districtName, person?.districtName),
    getText(item.addressProvince?.name, item.province?.name, item.provinceName, person?.provinceName),
    getText(item.postcode, item.zipcode, person?.postcode),
  ].filter(Boolean)
  return getText(item.fullAddress, item.addressText, person?.addressText, parts.join(' ')) || '-'
}

const OpdPatientSummary = ({ row, show, ipd = false }) => {
  const [pasHeader, setPasHeader] = useState({})
  const [clinicalHeader, setClinicalHeader] = useState({})
  const [visitInType, setVisitInType] = useState({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [photoError, setPhotoError] = useState(false)

  useEffect(() => {
    if (!show || !row?.hn) return undefined
    let active = true
    setPasHeader({})
    setClinicalHeader({})
    setPhotoError(false)
    setError('')
    setLoading(true)
    Promise.allSettled([
      httpClient.get(`/pas/patient-header/${ipd ? 'ipd' : 'opd'}`, { params: { hn: row.hn } }),
      httpClient.get('/clinical/patient-header/opd', { params: { hn: row.hn, vn: row.vn } }),
    ]).then(([pas, clinical]) => {
      if (!active) return
      if (pas.status === 'fulfilled') setPasHeader(getData(pas.value))
      if (clinical.status === 'fulfilled') setClinicalHeader(getData(clinical.value))
      if (pas.status === 'rejected' && clinical.status === 'rejected') {
        setError('โหลดข้อมูลเพิ่มเติมของผู้ป่วยไม่สำเร็จ')
      }
    }).finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [row?.hn, row?.vn, show, ipd])

  const patient = getFirst(pasHeader.patient)
  const person = getFirst(patient.person)
  const visit = getFirst(clinicalHeader.patientVisit)
  useEffect(() => {
    const visitInTypeId = visit.patientVisitInTypeId
    if (!show || !visitInTypeId) return undefined
    let active = true
    setVisitInType({})
    httpClient.get(`/core/patient-visit-in-type/${visitInTypeId}`)
      .then((response) => { if (active) setVisitInType(getData(response)) })
      .catch(() => { if (active) setVisitInType({}) })
    return () => { active = false }
  }, [visit.patientVisitInTypeId, show])
  const screening = getFirst(clinicalHeader.opdScreening)
  const vitalHistory = getScreeningValue(screening, ['vitalSignHistory'])
  const vitals = getFirst(Array.isArray(vitalHistory) ? [...vitalHistory].reverse() : [])
  const vital = (fields) => getText(...fields.map((field) => vitals[field]), getScreeningValue(screening, fields))
  const systolic = vital(['systolic', 'systolicBloodPressure'])
  const diastolic = vital(['diastolic', 'diastolicBloodPressure'])
  const allergy = (Array.isArray(clinicalHeader.drugAllergy) ? clinicalHeader.drugAllergy : [])
    .map((item) => getText(item.drugName, item.genericName, item.drug?.name, item.allergyName))
    .filter(Boolean).join(', ')
  const note = (Array.isArray(clinicalHeader.patientNote) ? clinicalHeader.patientNote : [])
    .map((item) => getText(item.noteContent, item.content, item.note, item.description))
    .find(Boolean)
  const attachmentId = getText(patient.attachmentId, patient.mediaAttachmentId, patient.attachment?.attachmentId)
  const imageUrl = attachmentId ? `${getBaseURL()}/media/attachment/thumbnail/${encodeURIComponent(attachmentId)}` : ''

  return (
    <div className="p-3 mb-4 bg-white border" style={{ borderRadius: 8 }}>
      {error && <Alert variant="warning">{error}</Alert>}
      <div className="d-flex align-items-start">
        <div className="mr-3 d-flex align-items-center justify-content-center" style={{ flex: '0 0 150px', height: 150, backgroundColor: '#ededed' }}>
          {imageUrl && !photoError
            ? <img alt="รูปผู้ป่วย" onError={() => setPhotoError(true)} src={imageUrl} style={{ height: '100%', width: '100%', objectFit: 'cover' }} />
            : <i aria-hidden="true" className="feather icon-user text-muted" style={{ fontSize: 34 }} />}
        </div>
        <div className="flex-grow-1" style={{ minWidth: 0 }}>
          <Row>
            <Col md={6} lg={4}><strong>HN:</strong> {display(row?.hn)}</Col>
            <Col md={6} lg={4}><strong>VN:</strong> {display(row?.vn)}</Col>
            <Col md={6} lg={4}><strong>ชื่อ:</strong> {display(getText(row?.patientName, person.fullName))}</Col>
            {ipd && <Col md={6} lg={4}><strong>AN:</strong> {display(row?.an)}</Col>}
            <Col md={6} lg={4}><strong>วันที่รับบริการ:</strong> {row?.visitDate && moment(row.visitDate).isValid() ? moment(row.visitDate).add(543, 'years').format('DD/MM/YYYY') : '-'}</Col>
            <Col md={6} lg={4}><strong>อายุ:</strong> {getAge(person, row)}</Col>
            <Col md={6} lg={4}><strong>แผนก:</strong> {display(row?.specialty?.name)}</Col>
            <Col md={6} lg={4}><strong>ห้องตรวจ:</strong> {display(row?.place?.name)}</Col>
            <Col md={6} lg={4}><strong>สิทธิ์:</strong> {display(getText(visit.sponsor?.name, row?.benefitType?.name, patient.benefitTypeName, row?.benefitTypeCode))}</Col>
            <Col md={6} lg={4}><strong>แพทย์:</strong> {display(getText(row?.doctor?.fullName, row?.doctorName))}</Col>
            <Col md={6} lg={4}><strong>ประเภทการมา:</strong> {display(getText(visit.patientVisitInType?.name, visitInType.name, visit.patientVisitType?.name, visit.visitType?.name, visit.visitTypeCode))}</Col>
            <Col md={6} lg={4}><strong>ประเภทผู้ป่วย:</strong> {display(getText(patient.patientClass?.name, visit.patientType?.name))}</Col>
            <Col xs={12}><strong>ที่อยู่:</strong> {getAddress(pasHeader.currentAddress, person)}</Col>
            <Col xs={12}><strong>CC:</strong> {display(getText(getScreeningValue(screening, ['chiefComplaint', 'cc'])))}</Col>
            {ipd && <>
              <Col md={6} lg={4}><strong>Ward:</strong> {display(row?.ward?.name)}</Col>
              <Col md={6} lg={4}><strong>เตียง:</strong> {display(row?.bedCode)}</Col>
              <Col md={6} lg={4}><strong>วัน Admit:</strong> {row?.checkInDate && moment(row.checkInDate).isValid() ? moment(row.checkInDate).add(543, 'years').format('DD/MM/YYYY') : '-'}</Col>
              <Col md={6} lg={4}><strong>วัน D/C:</strong> {row?.checkOutDate && moment(row.checkOutDate).isValid() ? moment(row.checkOutDate).add(543, 'years').format('DD/MM/YYYY') : '-'}</Col>
              <Col md={6} lg={4}><strong>วันนอน:</strong> {display(row?.lengthOfStay)}</Col>
              <Col md={6} lg={4}><strong>วันลา:</strong> {display(row?.leaveDay)}</Col>
              <Col md={6} lg={4}><strong>DRGs:</strong> {display(row?.drgResult?.drg)}</Col>
              <Col md={6} lg={4}><strong>น้ำหนัก:</strong> {display(row?.weight)}</Col>
            </>}
          </Row>
        </div>
      </div>
      <div className="mt-3"><strong>Vital Sign:</strong> BP {display(systolic)}/{display(diastolic)} mmHg, BT {display(vital(['temperature', 'bodyTemperature']))} °C, PR {display(vital(['pulse', 'pulseRate']))}/min, RR {display(vital(['respiratoryRate', 'respirationRate', 'respiratory']))}/min</div>
      <div><strong>ประวัติแพ้ยา:</strong> {display(allergy)}</div>
      {note && <Alert className="mt-2 mb-0 py-2" variant="danger"><strong>Note แจ้งเตือน:</strong> {note}</Alert>}
      {loading && <div className="text-muted mt-2"><Spinner animation="border" size="sm" /> กำลังโหลดข้อมูลผู้ป่วย...</div>}
    </div>
  )
}

export default OpdPatientSummary
